import type { LucideIcon } from 'lucide-react';
import { SidebarMenuBadge } from '@/components/ui/sidebar';
import { liveCountLabel } from '@/lib/live-count';

/**
 * A sidebar nav icon with a count bubble on its corner — the collapsed
 * rail's badge, where `SidebarMenuBadge` is hidden. Renders the bare icon
 * at zero.
 *
 * The wrapper is `flex` and exactly icon-sized: an inline wrapper would
 * take the text line-height and sit the svg on its baseline, pushing these
 * icons out of line with the sidebar's bare ones. The bubble overhangs by
 * 6px, which on the collapsed rail's 32px button (p-2 → icon at 8–24px)
 * keeps it 2px inside the button's clipped, rounded edge.
 */
export function NavIconWithCount({
  icon: Icon,
  count,
}: {
  icon: LucideIcon;
  count: number;
}): React.ReactElement {
  return (
    <span className="relative flex size-4 shrink-0">
      <Icon className="size-4" />
      {count > 0 ? (
        <span
          aria-hidden
          data-testid="nav-count-bubble"
          className="absolute -right-1.5 -top-1.5 hidden h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold leading-none text-primary-foreground group-data-[collapsible=icon]:flex"
        >
          {liveCountLabel(count)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * The expanded rail's count: a pill at the right of the row. Hidden by
 * `SidebarMenuBadge` itself on the collapsed rail, where
 * {@link NavIconWithCount} carries the number. Renders nothing at zero.
 */
export function NavCountPill({ count }: { count: number }): React.ReactElement | null {
  if (count <= 0) return null;
  return (
    <SidebarMenuBadge
      aria-hidden
      className="rounded-full bg-primary text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground"
    >
      {liveCountLabel(count)}
    </SidebarMenuBadge>
  );
}
